import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/api';
import { useTranslation } from '../../i18n';

const IntegrationsTab = () => {
  const { t } = useTranslation();
  const [connectedIntegrations, setConnectedIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncingTasks, setSyncingTasks] = useState(false);
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [refreshingWatch, setRefreshingWatch] = useState(false);
  const [watchChannelLastUpdate, setWatchChannelLastUpdate] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');
  const [error, setError] = useState('');
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [showCalendarSelect, setShowCalendarSelect] = useState(false);

  const AVAILABLE_INTEGRATIONS = [
    {
      id: 'google-tasks',
      name: 'Google Tasks',
      description: t('googleTasksDesc'),
      icon: '📋',
      color: '#4285f4',
      category: t('productivity')
    },
    {
      id: 'google-calendar',
      name: 'Google Calendar',
      description: t('googleCalendarDesc'),
      icon: '📅',
      color: '#4285f4',
      category: t('calendarCategory')
    },
    {
      id: 'notion',
      name: 'Notion',
      description: t('notionDesc'),
      icon: '📝',
      color: '#000000',
      category: t('productivity'),
      comingSoon: true
    },
    {
      id: 'slack',
      name: 'Slack',
      description: t('slackDesc'),
      icon: '💬',
      color: '#4a154b',
      category: t('communication'),
      comingSoon: true
    }
  ];

  const fetchIntegrationStatus = async () => {
    try {
      const googleStatus = await apiClient.get('/google/status');
      const isGoogleConnected = googleStatus.data.connected;
      const tasksEnabled = googleStatus.data.tasks_sync_enabled || false;
      const calendarEnabled = googleStatus.data.calendar_sync_enabled || false;
      const calendarWatchLastUpdate = googleStatus.data.calendar_watch_last_update;
      
      setConnectedIntegrations({
        'google-tasks': isGoogleConnected && tasksEnabled,
        'google-calendar': isGoogleConnected && calendarEnabled,
        'google-oauth': isGoogleConnected
      });
      
      setWatchChannelLastUpdate(calendarWatchLastUpdate);

      // Fetch calendars and selected calendar if Google is connected
      if (isGoogleConnected) {
        try {
          const [calendarsResponse, prefsResponse] = await Promise.all([
            apiClient.get('/user/google/calendars'),
            apiClient.get('/user/preferences')
          ]);
          
          setCalendars(calendarsResponse.data.calendars || []);
          
          // Set selected calendar from preferences
          const calendarId = prefsResponse.data.preferences?.google_calendar_id;
          if (calendarId) {
            setSelectedCalendar(calendarId);
          }
        } catch (err) {
          console.error('Error fetching calendars:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching integration status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleService = useCallback(async (integrationId, enabled) => {
    try {
      // For calendar, show selection dialog if enabling
      if (integrationId === 'google-calendar' && enabled) {
        setShowCalendarSelect(true);
        return;
      }

      const prefKey = integrationId === 'google-tasks' 
        ? 'google_tasks_sync_enabled' 
        : 'google_calendar_sync_enabled';
      
      const payload = {
        preferences: {
          [prefKey]: enabled
        }
      };

      // Add calendar ID if disabling calendar sync
      if (integrationId === 'google-calendar' && !enabled) {
        payload.preferences.google_calendar_id = null;
      }

      await apiClient.post('/user/preferences', payload);
      
      setConnectedIntegrations(prev => ({ 
        ...prev, 
        [integrationId]: enabled
      }));
      
      setSyncMessage(enabled ? t('serviceEnabled') : t('serviceDisabled'));
      
      setTimeout(() => setSyncMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling service:', error);
      setError(error.response?.data?.detail || t('failedToToggle') || 'Failed to toggle service');
    }
  }, [t]);

  const handleConnect = async (integrationId) => {
    if (integrationId === 'google-tasks' || integrationId === 'google-calendar') {
      // Determine service type for OAuth scopes
      const service = integrationId === 'google-calendar' ? 'calendar' : 'tasks';
      
      try {
        const response = await apiClient.get(`/google/auth?service=${service}`);
        // Store which service to enable after OAuth
        localStorage.setItem('google_service_to_enable', integrationId);
        window.location.href = response.data.auth_url;
      } catch (error) {
        console.error('Error connecting:', error);
        setError(t('failedToConnect') || 'Failed to connect');
      }
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (integrationId === 'google-tasks' || integrationId === 'google-calendar') {
      // Just disable the service, don't disconnect OAuth
      if (!window.confirm(t('disableServiceConfirm') || 'Disable this service?')) return;
      await handleToggleService(integrationId, false);
    }
  };

  const handleConfirmCalendar = async () => {
    if (!selectedCalendar) {
      setError(t('selectCalendarError') || 'Please select a calendar');
      return;
    }

    try {
      await apiClient.post('/user/preferences', {
        preferences: {
          google_calendar_sync_enabled: true,
          google_calendar_id: selectedCalendar
        }
      });

      setConnectedIntegrations(prev => ({ 
        ...prev, 
        'google-calendar': true
      }));
      
      setShowCalendarSelect(false);
      setSyncMessage(t('calendarSyncEnabled') || 'Calendar sync enabled!');
      setTimeout(() => setSyncMessage(''), 3000);
    } catch (error) {
      console.error('Error enabling calendar sync:', error);
      setError(error.response?.data?.detail || t('failedToEnableSync') || 'Failed to enable sync');
    }
  };

  const handleChangeCalendar = () => {
    setShowCalendarSelect(true);
  };

  const getSelectedCalendarName = () => {
    if (!selectedCalendar) return null;
    const calendar = calendars.find(cal => cal.id === selectedCalendar);
    return calendar ? calendar.summary : selectedCalendar;
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm(t('disconnectGoogleConfirm') || 'Disconnect Google? This will disable all Google services.')) return;
    
    try {
      await apiClient.post('/google/disconnect');
      setConnectedIntegrations(prev => ({ 
        ...prev, 
        'google-tasks': false,
        'google-calendar': false,
        'google-oauth': false
      }));
      setSyncMessage('');
    } catch (error) {
      console.error('Error disconnecting:', error);
      setError(t('failedToDisconnect') || 'Failed to disconnect');
    }
  };

  const handleSync = async (type = 'tasks') => {
    const setSyncing = type === 'calendar' ? setSyncingCalendar : setSyncingTasks;
    setSyncing(true);
    setSyncMessage('');
    setError('');
    
    try {
      const endpoint = type === 'calendar' ? '/google/calendar/sync' : '/google/sync';
      const response = await apiClient.post(endpoint);
      const data = response.data.synced || response.data;
      const count = data.synced_count || data.synced || 0;
      const message = type === 'calendar' 
        ? (t('syncedEvents') || `Synced ${count} events!`).replace('{count}', count)
        : (t('syncedTasks') || `Synced ${count} tasks!`).replace('{count}', count);
      setSyncMessage(message);
      setTimeout(() => setSyncMessage(''), 5000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('syncFailed') || 'Sync failed';
      setError(errorMessage);
      setTimeout(() => setError(''), 8000);
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshWatchChannel = async () => {
    setRefreshingWatch(true);
    setSyncMessage('');
    setError('');
    
    try {
      const response = await apiClient.post('/google/calendar/refresh-watch');
      const data = response.data;
      
      // Update the last update timestamp
      setWatchChannelLastUpdate(data.last_update);
      
      setSyncMessage(t('watchChannelRefreshed') || 'Watch channel refreshed successfully!');
      setTimeout(() => setSyncMessage(''), 5000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('failedToRefreshWatch') || 'Failed to refresh watch channel';
      setError(errorMessage);
      setTimeout(() => setError(''), 8000);
    } finally {
      setRefreshingWatch(false);
    }
  };

  const formatLastUpdateTime = (timestamp) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      return timestamp;
    }
  };

  useEffect(() => {
    fetchIntegrationStatus();
  }, []); // Only run once on mount

  useEffect(() => {
    // Check if we need to enable a service after OAuth
    const serviceToEnable = localStorage.getItem('google_service_to_enable');
    if (serviceToEnable) {
      localStorage.removeItem('google_service_to_enable');
      // Wait a bit for status to update, then enable ONLY the requested service
      setTimeout(async () => {
        await handleToggleService(serviceToEnable, true);
        // Don't refresh status - handleToggleService already updates state optimistically
      }, 1000);
    }
  }, [handleToggleService]);

  if (loading) return <div className="tab-loading">{t('loading')}</div>;

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>{t('integrations')}</h2>
        <p>{t('integrationsSubtitle')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {syncMessage && <div className="alert alert-success">{syncMessage}</div>}

      {connectedIntegrations['google-oauth'] && (
        <div className="alert alert-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>✅ {t('googleAccountConnected')}</span>
          <button className="btn btn-outline-danger btn-sm" onClick={handleDisconnectGoogle}>
            {t('disconnectGoogle')}
          </button>
        </div>
      )}

      {/* Calendar Selection Modal */}
      {showCalendarSelect && (
        <div className="modal-overlay" onClick={() => setShowCalendarSelect(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('selectCalendar') || 'Select Calendar'}</h3>
            <p>{t('selectCalendarDesc') || 'Choose which Google Calendar to sync with SmartGTD, or let us create a new one for you.'}</p>
            
            <select 
              value={selectedCalendar} 
              onChange={(e) => setSelectedCalendar(e.target.value)}
              className="form-select"
            >
              <option value="">{t('chooseCalendar') || '-- Choose a calendar --'}</option>
              <option value="__create_new__">{t('createNewCalendar') || '📅 Create new "SmartGTD" calendar'}</option>
              {calendars.map(cal => (
                <option key={cal.id} value={cal.id}>
                  {cal.primary ? '⭐ ' : ''}{cal.summary}
                </option>
              ))}
            </select>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowCalendarSelect(false)}>
                {t('cancel') || 'Cancel'}
              </button>
              <button className="btn btn-primary" onClick={handleConfirmCalendar}>
                {t('confirm') || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="integrations-grid">
        {AVAILABLE_INTEGRATIONS.map(integration => {
          const isConnected = connectedIntegrations[integration.id];
          const isComingSoon = integration.comingSoon;

          return (
            <div key={integration.id} className="integration-card">
              <div className="integration-icon" style={{ backgroundColor: integration.color }}>
                {integration.icon}
              </div>
              
              <div className="integration-info">
                <div className="integration-header">
                  <h3>{integration.name}</h3>
                  {isConnected && <span className="badge badge-success">{t('connected')}</span>}
                  {isComingSoon && <span className="badge badge-warning">{t('comingSoon')}</span>}
                </div>
                <p>{integration.description}</p>
                {integration.id === 'google-calendar' && isConnected && selectedCalendar && (
                  <div className="calendar-info">
                    <small>📅 {t('syncingTo') || 'Syncing to'}: <strong>{getSelectedCalendarName()}</strong></small>
                    {watchChannelLastUpdate && (
                      <div>
                        <small>🔄 {t('watchChannelLastUpdate') || 'Watch channel last updated'}: <strong>{formatLastUpdateTime(watchChannelLastUpdate)}</strong></small>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="integration-actions">
                {!isComingSoon && !isConnected && (
                  <button className="btn btn-primary" onClick={() => handleConnect(integration.id)}>
                    {connectedIntegrations['google-oauth'] && (integration.id === 'google-tasks' || integration.id === 'google-calendar')
                      ? t('enable')
                      : t('connect')}
                  </button>
                )}
                
                {!isComingSoon && isConnected && (
                  <>
                    {integration.id === 'google-tasks' && (
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => handleSync('tasks')}
                        disabled={syncingTasks}
                      >
                        {syncingTasks ? t('syncing') : t('syncNow')}
                      </button>
                    )}
                    {integration.id === 'google-calendar' && (
                      <>
                        <button 
                          className="btn btn-secondary" 
                          onClick={() => handleSync('calendar')}
                          disabled={syncingCalendar}
                        >
                          {syncingCalendar ? t('syncing') : t('syncNow')}
                        </button>
                        <button 
                          className="btn btn-secondary" 
                          onClick={handleRefreshWatchChannel}
                          disabled={refreshingWatch}
                        >
                          {refreshingWatch ? t('refreshingWatchChannel') : t('refreshWatchChannel')}
                        </button>
                        <button 
                          className="btn btn-primary" 
                          onClick={handleChangeCalendar}
                        >
                          {t('changeCalendar') || 'Change Calendar'}
                        </button>
                      </>
                    )}
                    <button 
                      className="btn btn-outline-danger" 
                      onClick={() => handleDisconnect(integration.id)}
                    >
                      {t('disable')}
                    </button>
                  </>
                )}
                
                {isComingSoon && (
                  <button className="btn btn-disabled" disabled>{t('comingSoon')}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IntegrationsTab;
